import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { BarChart3, Table as TableIcon } from 'lucide-react';
import { DailyMusterCount } from '../../services/musterCountService';

interface MusterCountChartProps {
    data: DailyMusterCount[];
    viewMode: 'graph' | 'table';
    onViewModeChange: (mode: 'graph' | 'table') => void;
}

export const MusterCountChart: React.FC<MusterCountChartProps> = ({ data, viewMode, onViewModeChange }) => {
    // Format data for the chart
    const chartData = data.map((item) => ({
        date: item.date,
        musterRollCount: item.musterRollCount,
        // attendanceCount: item.attendanceCount,
    }));

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const formatTooltipValue = (value: number) => {
        return value.toLocaleString();
    };

    const formatTooltipLabel = (label: string) => {
        const date = new Date(label);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    if (data.length === 0) {
        return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
                <Card>
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div>
                                <CardTitle className="text-xl sm:text-2xl">Daily Muster Count Trends</CardTitle>
                                <CardDescription className="text-xs sm:text-sm">Muster roll count and manpower generated over time</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <BarChart3 className={`w-4 h-4 ${viewMode === 'graph' ? 'text-brand' : 'text-gray-400'}`} />
                                <Switch
                                    checked={viewMode === 'table'}
                                    onChange={(e) => onViewModeChange(e.target.checked ? 'table' : 'graph')}
                                />
                                <TableIcon className={`w-4 h-4 ${viewMode === 'table' ? 'text-brand' : 'text-gray-400'}`} />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64 sm:h-80 md:h-96 flex items-center justify-center text-gray-500">
                            <p className="text-sm sm:text-base px-4 text-center">No data available. Please apply filters to view the chart.</p>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        );
    }

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <Card>
                <CardHeader>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <CardTitle className="text-xl sm:text-2xl">Daily Muster Count Trends</CardTitle>
                            <CardDescription className="text-xs sm:text-sm">Muster roll count and manpower generated over time</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <BarChart3 className={`w-4 h-4 ${viewMode === 'graph' ? 'text-brand' : 'text-gray-400'}`} />
                            <Switch
                                checked={viewMode === 'table'}
                                onChange={(e) => onViewModeChange(e.target.checked ? 'table' : 'graph')}
                            />
                            <TableIcon className={`w-4 h-4 ${viewMode === 'table' ? 'text-brand' : 'text-gray-400'}`} />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="h-64 sm:h-80 md:h-96 w-full overflow-x-auto">
                        <div className="min-w-[600px] h-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={chartData}
                                    margin={{
                                        top: 10,
                                        right: 10,
                                        left: 0,
                                        bottom: 40,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={formatDate} 
                                        stroke="#666" 
                                        fontSize={10}
                                        angle={-45} 
                                        textAnchor="end" 
                                        height={60}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis 
                                        stroke="#666" 
                                        fontSize={10}
                                        width={50}
                                        tickFormatter={(value) => {
                                            if (value >= 1000) {
                                                return `${(value / 1000).toFixed(0)}K`;
                                            }
                                            return value.toString();
                                        }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#fff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                            fontSize: '12px',
                                        }}
                                        labelFormatter={formatTooltipLabel}
                                        formatter={(value: number, name: string) => {
                                            const label = name === 'musterRollCount' ? 'Muster Roll Count' : /* name === 'attendanceCount' ? 'Manpower generated' : */ name;
                                            return [formatTooltipValue(value), label];
                                        }}
                                    />
                                    <Legend 
                                        wrapperStyle={{ fontSize: '12px' }}
                                        iconSize={12}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="musterRollCount" 
                                        stroke="#3b82f6" 
                                        strokeWidth={2} 
                                        dot={{ r: 2 }} 
                                        activeDot={{ r: 4 }} 
                                        name="Muster Roll Count" 
                                    />
                                    {/* <Line 
                                        type="monotone" 
                                        dataKey="attendanceCount" 
                                        stroke="#10b981" 
                                        strokeWidth={2} 
                                        dot={{ r: 2 }} 
                                        activeDot={{ r: 4 }} 
                                        name="Manpower generated" 
                                    /> */}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};
